import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeft, ChevronRight, Info, PackageOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import AccessRestricted from '../../../components/AccessRestricted';
import { AbilityContext } from '../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../constants/permissions.constants';
import { Branch } from '../../../types/lists.types';
import { PurchaseOrderSummary } from '../../../types/procurement.types';
import { ApiError } from '../../../utils/api';
import { branchesApi } from '../../../utils/lists-api';
import { purchaseOrdersApi } from '../../../utils/procurement-api';
import { usePageTitle } from '../../../utils/usePageTitle';
import { formatDate, orderReference, vendorName } from './procurement-ui';
import { PurchaseOrderStatusPill } from './components/PurchaseOrderStatus';

const serverMessage = (err: unknown, fallback: string) => {
    if (err instanceof ApiError) {
        // Error bodies are `{ message }` — see the backend route conventions.
        const body = err.data as { message?: string } | null;
        return body?.message || err.message || fallback;
    }
    return err instanceof Error ? err.message || fallback : fallback;
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Receiving queue — the list a yard opens on a phone when a delivery turns up.
 *
 * Issued and part-received POs only, earliest required-by first, because that is the order
 * the yard actually works in. Every row opens the capture flow.
 */
export default function ReceivingQueuePage() {
    usePageTitle();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.PURCHASE_ORDER);

    const [orders, setOrders] = useState<PurchaseOrderSummary[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [branchId, setBranchId] = useState('');
    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            // Two statuses make up the queue; asking for them separately keeps the page's
            // ordering rule obvious rather than filtering a huge list client-side.
            const [issued, receiving] = await Promise.all([
                purchaseOrdersApi.list({ status: 'po_issued', branchId: branchId || undefined, limit: '100' }),
                purchaseOrdersApi.list({ status: 'receiving', branchId: branchId || undefined, limit: '100' }),
            ]);

            const seen = new Set<number>();
            const merged = [...issued.orders, ...receiving.orders].filter((order) => {
                if (seen.has(order.id)) return false;
                seen.add(order.id);
                // A PO with nothing outstanding is between states, not waiting on the yard.
                return order.orderedUnits - order.receivedUnits > 0;
            });
            merged.sort((a, b) =>
                (a.requiredByDate ?? '9999-12-31').localeCompare(b.requiredByDate ?? '9999-12-31'));

            setOrders(merged);
            setAccessDenied(null);
        } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
                setAccessDenied(err.data?.message || err.message);
                return;
            }
            toast.error(serverMessage(err, 'Failed to load the receiving queue'));
        } finally {
            setLoading(false);
        }
    }, [branchId]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    useEffect(() => {
        if (!canRead) return;
        branchesApi.list({ status: 'active' }).then(setBranches).catch(() => undefined);
    }, [canRead]);

    if (!canRead) return <AccessRestricted />;

    if (accessDenied) {
        return (
            <AccessRestricted
                accessMessage={accessDenied}
                backMessage="Back to Purchase Orders"
                navigateTo="/stock/procurement/purchase-orders"
            />
        );
    }

    const cutoff = today();

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-3xl">
            <div>
                <Button variant="ghost" size="sm" className="mb-2 -ml-2"
                        onClick={() => navigate('/stock/procurement')}>
                    <ArrowLeft size={16} className="mr-1.5" />Procurement
                </Button>
                <h1 className="text-2xl font-bold">Receiving</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Purchase orders with something still to arrive.
                </p>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 text-blue-900 flex gap-2">
                <Info size={16} className="shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed">
                    A receipt never closes the PO on its own — the order stays here until every
                    line is fully received, or it is closed short. Partial deliveries are the
                    norm, so receiving part of a delivery is a complete, valid action.
                </p>
            </div>

            <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="receiving-branch">
                    Branch
                </label>
                <Select
                    value={branchId || 'all'}
                    onValueChange={(value) => setBranchId(value === 'all' ? '' : value)}
                >
                    <SelectTrigger id="receiving-branch" className="mt-1">
                        <SelectValue placeholder="All branches" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All branches</SelectItem>
                        {branches.map((branch) => (
                            <SelectItem key={branch.id} value={String(branch.id)}>{branch.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => <div key={i} className="h-20 bg-gray-300 rounded-md" />)}
                </div>
            ) : orders.length === 0 ? (
                <Card>
                    <CardContent className="p-6 flex flex-col items-center text-center gap-2">
                        <PackageOpen size={36} className="text-muted-foreground" />
                        <p className="font-medium">Nothing to receive</p>
                        <p className="text-sm text-muted-foreground">
                            A purchase order appears here once it has been issued and still has
                            outstanding quantities.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-2">
                    {orders.map((order) => {
                        const outstanding = order.orderedUnits - order.receivedUnits;
                        const due = order.requiredByDate ? order.requiredByDate.slice(0, 10) : null;
                        const overdue = due !== null && due < cutoff;
                        return (
                            <button
                                key={order.id}
                                type="button"
                                onClick={() => navigate(`/stock/procurement/receiving/${order.id}`)}
                                className="w-full text-left flex items-center gap-3 rounded-lg border border-border bg-card p-3 min-h-[44px] hover:bg-accent transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-sm font-medium">
                                            {orderReference(order)}
                                        </span>
                                        <PurchaseOrderStatusPill status={order.status} />
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1 truncate">
                                        {vendorName(order.vendor)} · {order.project?.name ?? order.branch.name}
                                    </p>
                                    <p className="text-xs mt-1">
                                        <span className="font-medium">{outstanding} units outstanding</span>
                                        <span className={overdue ? 'text-amber-800' : 'text-muted-foreground'}>
                                            {' · due '}{formatDate(order.requiredByDate)}
                                            {overdue ? ' — overdue' : ''}
                                        </span>
                                    </p>
                                </div>
                                <ChevronRight size={18} className="text-muted-foreground shrink-0" />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
